import { Pattern } from './Pattern';
import { PatternType, Difficulty, Bounds, Vector2, DotState } from '../types';

/**
 * CrossFire - Two perpendicular walls of dots sweep the arena simultaneously
 * from opposite sides. Each wall has a gap. The horizontal wall moves
 * vertically; the vertical wall moves horizontally. They respawn when they
 * leave the arena so the threat is constant during the pattern duration.
 */
export class CrossFire extends Pattern {
  readonly type = PatternType.CROSS_FIRE;
  difficulty: Difficulty = Difficulty.HARD;

  private readonly duration: number = 14000;
  private readonly dotSpacing: number = 22;
  private readonly gapSizeDots: number = 4;
  private sweepSpeed: number = 130;
  private hasSpawned: boolean = false;
  private respawnIntervalMs: number = 4500;
  private timeSinceRespawn: number = 0;

  // Two walls
  private horizontalY: number = 0;
  private horizontalVelY: number = 0;
  private horizontalGapCenter: number = 0;
  private verticalX: number = 0;
  private verticalVelX: number = 0;
  private verticalGapCenter: number = 0;
  private wallsActive: boolean = false;

  constructor(difficulty: Difficulty = Difficulty.HARD) {
    super();
    this.difficulty = difficulty;
    switch (difficulty) {
      case Difficulty.HARD:
        this.sweepSpeed = 160;
        break;
      case Difficulty.MEDIUM:
        this.sweepSpeed = 120;
        break;
      default:
        this.sweepSpeed = 90;
    }
  }

  spawn(_center: Vector2, bounds: Bounds): void {
    this.start();
    this.launchWalls(bounds);
    this.hasSpawned = true;
  }

  private launchWalls(bounds: Bounds): void {
    // Randomize: sometimes swap which side each wall starts from
    const horizFromTop = Math.random() > 0.5;
    this.horizontalY = horizFromTop ? 0 : bounds.height;
    this.horizontalVelY = horizFromTop ? this.sweepSpeed : -this.sweepSpeed;
    this.horizontalGapCenter = bounds.width * (0.15 + Math.random() * 0.7);

    const vertFromLeft = Math.random() > 0.5;
    this.verticalX = vertFromLeft ? 0 : bounds.width;
    this.verticalVelX = vertFromLeft ? this.sweepSpeed : -this.sweepSpeed;
    this.verticalGapCenter = bounds.height * (0.15 + Math.random() * 0.7);

    this.spawnWallDots(bounds);
    this.wallsActive = true;
    this.timeSinceRespawn = 0;
  }

  private spawnWallDots(bounds: Bounds): void {
    const horizDotCount = Math.floor(bounds.width / this.dotSpacing);
    const vertDotCount = Math.floor(bounds.height / this.dotSpacing);
    const gapWidth = this.gapSizeDots * this.dotSpacing;

    for (let i = 0; i < horizDotCount; i++) {
      const x = i * this.dotSpacing + this.dotSpacing / 2;
      if (Math.abs(x - this.horizontalGapCenter) < gapWidth / 2) continue;
      const dot = this.spawnDot(x, this.horizontalY, { x: 0, y: this.horizontalVelY });
      dot.skipSpawnAnimation();
      dot.state = DotState.ACTIVE;
      dot.patternId = this.type;
    }

    for (let i = 0; i < vertDotCount; i++) {
      const y = i * this.dotSpacing + this.dotSpacing / 2;
      if (Math.abs(y - this.verticalGapCenter) < gapWidth / 2) continue;
      const dot = this.spawnDot(this.verticalX, y, { x: this.verticalVelX, y: 0 });
      dot.skipSpawnAnimation();
      dot.state = DotState.ACTIVE;
      dot.patternId = this.type;
    }
  }

  update(dt: number, playerPosition: Vector2, bounds: Bounds): void {
    if (this.wallsActive) {
      this.horizontalY += this.horizontalVelY * dt;
      this.verticalX += this.verticalVelX * dt;

      // When both walls have exited, schedule respawn
      const horizontalOff =
        this.horizontalY < -50 || this.horizontalY > bounds.height + 50;
      const verticalOff =
        this.verticalX < -50 || this.verticalX > bounds.width + 50;
      if (horizontalOff && verticalOff) {
        this.wallsActive = false;
        this.timeSinceRespawn = 0;
      }
    } else if (this.elapsedMs <= this.duration) {
      this.timeSinceRespawn += dt * 1000;
      if (this.timeSinceRespawn >= this.respawnIntervalMs) {
        this.launchWalls(bounds);
      }
    }

    // Update dots - they move with their initial velocity (set at spawn)
    for (let i = this.dots.length - 1; i >= 0; i--) {
      const dot = this.dots[i];
      if (dot.isFrozen()) {
        dot.update(dt, bounds, playerPosition);
        continue;
      }
      const pos = dot.getPosition();
      if (pos.x < -80 || pos.x > bounds.width + 80 ||
          pos.y < -80 || pos.y > bounds.height + 80) {
        this.dots.splice(i, 1);
        continue;
      }
      dot.update(dt, bounds, playerPosition);
    }
  }

  isComplete(): boolean {
    return this.hasSpawned && this.elapsedMs > this.duration && this.dots.length === 0;
  }
}
